<template>
    <div>
        <b-table
        :sort-by.sync="pre_sort"
        :sort-desc="true"
        :items="row_data"
        :fields="header_fields"
        thead-class="position-sticky"
        striped
        head-variant="dark"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-compare="sortCompare"
        :filter-included-fields="filterOn"
        hover
        small
        >
        <template #head(checkbox)="data ">
            <b-form-group>
            <b-form-checkbox
                dis
                size="lg"
                @change="onSelectAllRows"
                v-model="all_rows_selected"
                variant="danger"
                class="mr-1 ml-2 _red-checkbox"
            >
            </b-form-checkbox>
            </b-form-group>
        </template>

        <template #head(customData)="data">
            {{ data.label }}
            <button @click="$emit('refreshResults')" class="btn btn-light ml-3">
            UPDATE TABLE
            </button>
        </template>

        <template #cell(checkbox)="data">
            <b-form-group>
            <b-form-checkbox
                size="lg"
                variant="danger"
                v-model="data.item.rowSelected"
                class="mr-1 _red-checkbox"
            >
            </b-form-checkbox>
            </b-form-group>
        </template>

        <template #cell(customData)="data">
            <div class="row justify-content-between pl-2 pr-5">
                <div>
                <h5 class="text-left pl-3">
                    <a
                    class="text-vb-primary"
                    style="font-weight: 500; font-size: 20px"
                    :href="data.value.href_link"
                    >{{ data.value.projectName }}
                    <font-awesome-icon
                        :icon="['fas', 'medal']"
                        v-if="isInCurrentQuarter(data.item.sales_start_date)"
                    /></a>
                </h5>
                <p
                    class="text-left pl-3 text-vb-primary mb-0 pb-0"
                    style="font-weight: 400; font-size: 18px"
                >
                    {{ data.value.project }}
                </p>
                </div>
            </div>
        </template>

        <template #cell(customLocationData)="data">
          <p
            class="text-left pl-3 text-vb-primary mb-0 pb-0"
            style="font-weight: 400; font-size: 18px"
          >
            {{ data.value.address }}
          </p>
          <p
            v-if="data.value.municipality"
            class="text-left pl-3 text-vb-primary mb-0 pb-0"
            style="font-weight: 400; font-size: 18px"
          >
            {{ data.value.municipality }}
          </p>
          <p
            v-else
            class="text-left pl-3 text-vb-primary mb-0 pb-0"
            style="font-weight: 400; font-size: 18px"
          >
            {{ data.value.neighbour }}
          </p>
        </template>

        <template #cell(field_mls_address_value)="data">
          <span
            v-if="data.item.field_mls_floor_plan_fid != null"
            style="cursor: pointer"
            class="text-primary"
            @click="getFloorPlan(data.item.field_mls_floor_plan_fid)"
            ><u>{{ data.value }}</u></span
          >
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(custom_field_mls_beds_value)="data">
          <strong
            class="font-weight-bold"
            >{{ data.value.type }}</strong
          >
          <br />
          {{ data.value.fp_specs }}
        </template>
        <template #cell(custom_listing_type)="data">
          <div v-if="isBusy">
            <b-spinner small variant="vb-secondary"></b-spinner>
          </div>
          <div v-else>
            <span v-if="data.value" :class="listingTypePillClass(data.value)">
              <span class="rpd-lt-pill__dot"></span>{{ data.value }}
            </span>
            <span v-else>&#8212;</span>
          </div>
        </template>
        <template #cell(field_mls_list_price_value)="rowData">
          <span
            v-if="Number(rowData.value) > 0"
            v-b-modal="'mlspop'"
            @click="$emit('popHistory', rowData.item)"
          >
            <span :style="{ color: setColor(rowData.item.price_backup) }"
              >${{ Math.round(rowData.value).toLocaleString() }}</span
            >
            <template
              v-if="
                Number(
                  getPriceChange(rowData.item.price_second, rowData.value)
                    .diffPercentage
                ) != 0
              "
            >
              (<span
                class="font-weight-bold"
                :style="{
                  color: getPriceChange(
                    rowData.item.price_second,
                    rowData.value
                  ).color,
                }"
                >{{
                  Number(
                    getPriceChange(rowData.item.price_second, rowData.value)
                      .diffPercentage
                  ).toLocaleString()
                }}%</span
              ><font-awesome-icon
                class="font-weight-bold"
                :icon="
                  Number(rowData.value) > 0
                    ? [
                        'fas',
                        getPriceChange(rowData.item.price_second, rowData.value)
                          .icon,
                      ]
                    : ''
                "
              />)
            </template>
          </span>
          <span v-else>-</span>
        </template>
        <template #cell(net_price)="rowData">
          <span
            v-if="Number(rowData.value) > 0"
            v-b-modal="'mlspop'"
            @click="$emit('popHistory', rowData.item)"
          >
            <span
              >${{ Math.round(rowData.value).toLocaleString() }}</span
            >
          </span>
          <span v-else>-</span>
        </template>

        <template #cell()="data">
          <span v-b-modal="'mlspop'" @click="$emit('popHistory', data.item)">{{
                data.value
          }}</span>
        </template>
    </b-table>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, onMounted, watch } from "vue";
import { listingTypePillClass } from "../../composables/listingTypePill";

const props = defineProps({
  row_data: [],
  header_fields: [],
  perPage: null,
  currentPage: null,
  marketStatus: null,
  filterOn: [],
  isBusy: false
});

const emit = defineEmits([
  "popModal",
  "updateTotalRows",
  "selectAllRows",
  "popHistory",
  "countProjectSelected",
  "getFilteredData",
]);
const sortCompare = (
  a,
  b,
  key,
  sortDesc,
  formatter,
  compareOptions,
  compareLocale
) => {
  const aDate = new Date(a[key]);
  const bDate = new Date(b[key]);
  if (
    [
      "sales_start_date",
      "mls_price_date",
      "price_date",
      "rz_sub_date",
      "dp_sub_date",
      "sold_date",
      "updated",
      "occupancy_date",
      "mls_sold_date",
    ].includes(key)
  ) {
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      let aToNum = aDate.getTime();
      let bToNum = bDate.getTime();

      return aToNum < bToNum ? -1 : aToNum > bToNum ? 1 : 0;
    }
    if (sortDesc) {
      if (isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) return -1;

      if (isNaN(bDate.getTime()) && !isNaN(aDate.getTime())) return 1;

      return 0;
    } else {
      if (isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) return 1;

      if (isNaN(bDate.getTime()) && !isNaN(aDate.getTime())) return -1;

      return 0;
    }
  } else {
    if (["customConstructionStatus", "construction_status"].includes(key)) {
      const dataA = a[key];
      const dataB = b[key];
      const customOrder = {
        "": 0,
        "Pre-Construction": 1,
        "In Process": 2,
        "Excavation & Foundation": 3,
        Framing: 4,
        "Interior Finishing": 5,
        "Some Homes Complete": 6,
        Complete: 7,
      };

      if (customOrder[dataA] != undefined && customOrder[dataB] !== undefined) {
        return customOrder[dataA] < customOrder[dataB]
          ? -1
          : customOrder[dataA] > customOrder[dataB]
          ? 1
          : 0;
      }
      if (sortDesc) {
        if (
          customOrder[dataA] === undefined &&
          customOrder[dataB] !== undefined
        )
          return -1;

        if (
          customOrder[dataB] === undefined &&
          customOrder[dataA] !== undefined
        )
          return 1;

        return 0;
      } else {
        if (
          customOrder[dataA] === undefined &&
          customOrder[dataB] !== undefined
        )
          return 1;

        if (
          customOrder[dataB] === undefined &&
          customOrder[dataA] !== undefined
        )
          return -1;

        return 0;
      }
    }

    if (["customRz", "customDp", "b"].includes(key)) {
      const dataA = a[key];
      const dataB = b[key];

      const customOrder =
        key == "customRz"
          ? {
              Adopted: 0,
              Approved: 1,
              "Second Reading": 2,
              "First Reading": 3,
              "In Process": 4,
              Submitted: 5,
              "N/A": 6,
            }
          : {
              "Not Submitted": 0,
              Submitted: 1,
              "In Process": 2,
              Approved: 3,
              Issued: 4,
              Completed: 5,
            };

      if (customOrder[dataA] != undefined && customOrder[dataB] !== undefined)
        return customOrder[dataA] - customOrder[dataB];
      else return -1;
    }

    if (
      [
        "mls_psf",
        "buyer_incentives",
        "custom_net_psf",
        "mls_list_price",
        "list_price_net",
        "total_net_psf",
        "custom_total_avg_price",
        "total_avg_price",
        "total_avg_psf",
      ].includes(key)
    ) {
      let aToNum =
        typeof a[key] == "number"
          ? Number(a[key])
          : Number(a[key].replace(/[$,]/g, ""));
      let bToNum =
        typeof b[key] == "number"
          ? Number(b[key])
          : Number(b[key].replace(/[$,]/g, ""));
      if (!isNaN(aToNum) && !isNaN(bToNum) && aToNum > 0 && bToNum > 0) {
        return aToNum < bToNum ? -1 : aToNum > bToNum ? 1 : 0;
      }
      if (sortDesc) {
        if ((isNaN(aToNum) || aToNum <= 0) && (!isNaN(bToNum) || bToNum > 0))
          return -1;

        if ((isNaN(bToNum) || bToNum <= 0) && (!isNaN(aToNum) || aToNum > 0))
          return 1;

        return 0;
      } else {
        if ((isNaN(aToNum) || aToNum <= 0) && (!isNaN(bToNum) || bToNum > 0))
          return 1;

        if ((isNaN(bToNum) || bToNum <= 0) && (!isNaN(aToNum) || aToNum > 0))
          return -1;

        return 0;
      }
    }
  }
};
const pre_sort = ref("");

const isInCurrentQuarter = (date) => {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const salesDate = new Date(date);
  const salesQuarter = Math.floor(salesDate.getMonth() / 3) + 1;

  return (
    salesDate.getFullYear() === now.getFullYear() &&
    salesQuarter === currentQuarter
  );
};

const getPriceChange = (oldValue, newValue) => {
  let result = {
    diffPercentage: "",
    color: "",
    icon: "",
  };

  if (newValue == "-" || !oldValue)
    return (result = {
      diffPercentage: "",
      color: "",
      icon: "",
    });

  result.diffPercentage = Number(
    ((newValue - oldValue) / oldValue) * 100
  ).toFixed(2);
  if (Number(result.diffPercentage) > 0) {
    result.color = "green";
    result.icon = "fa-long-arrow-alt-up";
  } else if (Number(result.diffPercentage) < 0) {
    result.color = "red";
    result.icon = "fa-long-arrow-alt-down";
  } else {
    result.color = "black";
    result.icon = "minus";
  }

  return result;
};

const getFloorPlan = (data) => {
  emit("popModal", data);
};

const setColor = (data) => {
  let color = "";
  if (data) {
    switch (data.toLocaleLowerCase()) {
      case "verified":
        color = "#0b36dc";
        break;

      case "educated estimated":
        color = "#dc3545";
        break;

      case "semi-verified":
        color = "#FF6F16";
        break;

      default:
        color = "#000";
        break;
    }
  }
  return color;
};

const all_rows_selected = ref(false);

const onSelectAllRows = () => {
  emit("selectAllRows", {
    selectAll: all_rows_selected.value,
    tableRef: null,
  });
};
onMounted(() => {
  pre_sort.value = "sales_start_date";
});
</script>

<style scoped>

</style>
